import MenuIcon from "@assets/icons/menu-vertical.svg";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { AnimatedHalo } from "@ui/components/onboarding/animated-halo";
import { row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { Menu } from "primereact/menu";
import { classNames } from "primereact/utils";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";

export enum MenuPosition {
	DefaultPosition = "DefaultPosition",
	CenteredPosition = "CenteredPosition",
}

export enum IconPosition {
	Left = "Left",
	Right = "Right",
}

export interface MenuItemProps {
	text: string;
	onClick: () => void;
	itemClassName?: string;
	icon?: ReactNode | JSX.Element;
	destructive?: boolean;
	disabled?: boolean;
	testId?: string;
	shouldDisplayHaloItem?: boolean;
}

interface MenuButtonProps {
	className?: string;
	menuItems: MenuItemProps[];
	menuId?: string;
	menuPosition?: MenuPosition;
	menuIconSize?: number;
	menuIconColor?: string;
	iconPosition?: IconPosition;
	itemIconColor?: string;
	shouldDisplayHalo?: boolean;
	onMenuClick?: () => void;
}

export const MenuButton: React.FC<MenuButtonProps> = ({
	className,
	menuItems,
	menuId,
	menuPosition = MenuPosition.DefaultPosition,
	iconPosition = IconPosition.Left,
	menuIconSize = 16,
	menuIconColor = colors.grey,
	itemIconColor,
	shouldDisplayHalo = false,
	onMenuClick,
}) => {
	const menuRef = useRef<Menu>(null);
	const [isOpened, setIsOpened] = useState(false);
	const [isHovering, setIsHovering] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const popupId = useMemo(() => menuId ?? "menu_button_popup_menu", [menuId]);
	const [overriddenMenuPosition, setOverriddenMenuPosition] = useState<"left" | "topLeft" | "right" | "topRight">(
		"right",
	);

	const items = useMemo(
		() =>
			menuItems.map(
				({ itemClassName, disabled, destructive, text, icon, shouldDisplayHaloItem = false, testId, onClick }) => {
					let labelColor: string | undefined;
					if (disabled) {
						labelColor = colors.grey50;
					} else if (destructive) {
						labelColor = colors.rejection;
					} else {
						labelColor = undefined;
					}

					let overriddenItemIconColor: string | undefined;
					if (disabled) {
						overriddenItemIconColor = colors.grey50;
					} else if (destructive) {
						overriddenItemIconColor = colors.rejection;
					} else {
						overriddenItemIconColor = itemIconColor;
					}

					return {
						template: (
							<AnimatedHalo shouldDisplayHalo={shouldDisplayHaloItem}>
								<Option
									data-test={testId}
									color={labelColor}
									disabled={disabled}
									onClick={event => {
										if (!disabled) {
											event.stopPropagation();
											onClick();
											menuRef.current?.hide(event);
										}
									}}
									className={itemClassName}
									isAnimatedBackground={shouldDisplayHaloItem}
								>
									{icon && iconPosition === IconPosition.Left && (
										<IconContainer color={overriddenItemIconColor}>{icon}</IconContainer>
									)}
									<span>{text}</span>
									{icon && iconPosition === IconPosition.Right && (
										<IconContainer color={overriddenItemIconColor}>{icon}</IconContainer>
									)}
								</Option>
							</AnimatedHalo>
						),
					};
				},
			),
		[menuItems, itemIconColor, iconPosition],
	);

	const centeredTransform = useMemo(() => {
		switch (overriddenMenuPosition) {
			case "right":
				return "translate(32px, calc(-20px - 50%))";
			case "topRight":
				return "translate(8px, 8px)";
			case "left":
				return "translate(-32px, calc(-20px - 50%))";
			case "topLeft":
				return "translate(-8px, 8px)";
		}
	}, [overriddenMenuPosition]);

	const defaultTransform = useMemo(() => {
		let transform: string;
		if (overriddenMenuPosition === "right") {
			transform = "translate(8px, -8px)";
		} else if (overriddenMenuPosition === "topRight") {
			transform = "translate(8px, 8px)";
		} else if (overriddenMenuPosition === "left") {
			transform = "translate(-8px, -8px)";
		} else {
			transform = "translate(-8px, 8px)";
		}
		return transform;
	}, [overriddenMenuPosition]);

	useEffect(() => {
		if (!isOpened) return;
		const buttonBox = buttonRef.current?.getBoundingClientRect();
		const popupBox = document.getElementById(popupId)?.getBoundingClientRect();

		if (popupBox && buttonBox) {
			if (popupBox.bottom <= buttonBox.bottom) {
				setOverriddenMenuPosition(popupBox.left <= buttonBox.left ? "topLeft" : "topRight");
			} else {
				setOverriddenMenuPosition(popupBox.left <= buttonBox.left ? "left" : "right");
			}
		}
	}, [isOpened, popupId]);

	return (
		<div className={className}>
			<ToggleOptionButton
				data-test={e2eTestId.createTemplatePage.toggleOptionButton}
				ref={buttonRef}
				onMouseEnter={() => {
					setIsHovering(true);
				}}
				onMouseOver={() => {
					setIsHovering(true);
				}}
				onMouseLeave={() => {
					!isOpened && setIsHovering(false);
				}}
				onClick={event => {
					menuRef.current?.toggle(event);
					onMenuClick?.();
					event.stopPropagation();
				}}
				aria-controls="popup_menu"
				aria-haspopup
			>
				<AnimatedHalo shouldDisplayHalo={shouldDisplayHalo}>
					<MenuIconContainer className={classNames("menu-button", { hover: isHovering })}>
						<MenuIcon width={menuIconSize} height={menuIconSize} color={menuIconColor} />
					</MenuIconContainer>
				</AnimatedHalo>
			</ToggleOptionButton>
			<Menu
				style={{
					backgroundColor: colors.white,
					padding: "8px 0px",
					transform: menuPosition === MenuPosition.CenteredPosition ? centeredTransform : defaultTransform,
					width: "auto",
				}}
				model={items}
				popup
				onShow={() => setIsOpened(true)}
				onHide={() => {
					setIsHovering(false);
					setIsOpened(false);
				}}
				ref={menuRef}
				id={popupId}
			/>
		</div>
	);
};

const IconContainer = styled.div<{ color?: string }>`
	${row(undefined, "center", "center")};

	> svg {
		width: 16px;
		height: 16px;
		color: ${({ color }) => color ?? colors.grey};
	}
`;

const ToggleOptionButton = styled.button`
	cursor: pointer;
	position: relative;
	appearance: none;
	border: none;
	padding: 8px;
	background-color: ${colors.transparent};
	${row(undefined, "center", "center")};
`;

const MenuIconContainer = styled.div`
	${row(undefined, "center", "center")};
	width: 24px;
	height: 24px;
	border-radius: 50%;
`;

const Option = styled.div<{ color?: string; disabled?: boolean; isAnimatedBackground?: boolean }>`
	padding: 4px 24px 4px 8px;
	width: 100%;
	${row(8, "flexstart", "center")};
	cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};

	span {
		color: ${props => props.color ?? colors.grey};
	}

	:hover {
		background-color: ${({ disabled }) => (disabled ? "unset" : `${colors.grey10}`)};
		border-radius: ${({ isAnimatedBackground }) => (isAnimatedBackground ? 40 : 0)}px;
	}
`;
